// These are select options and thus stored in the DB as strings
export enum PurchaseOrderStatus {
	/**
	 * @description PO is not yet submitted.
	 */
	'NEW' = '0',
	/**
	 * @description PO is submitted but not yet approved.
	 */
	'PENDING_APPROVAL' = '1',
	/**
	 * @description PO is approved but not yet sent to the vendor.
	 */
	'APPROVED' = '2',
	/**
	 * @description PO is sent to the vendor for fulfillment.
	 */
	'RELEASED' = '3',
	/**
	 * @description A partial fullfillment from the vendor has been received.
	 */
	'PARTIALLY_RECEIVED' = '4',
	/**
	 * @description PO has been fully fulfilled by the vendor and received.
	 */
	'RECEIVED' = '5',
	/**
	 * @description PO is held for some reason.
	 */
	'HELD' = '6',
	/**
	 * @description PO is cancelled for any reason.
	 */
	'CANCELLED' = '7',
	/**
	 * @description PO is backordered by the vendor.
	 */
	'BACKORDERED' = '8',
	/**
	 * @description PO was not approved after being submitted.
	 */
	'REJECTED' = '9',
	/**
	 * @description Vendor quote captured as a PO; becomes a committed PO on release.
	 */
	'QUOTE' = '10',
}

export type PurchaseOrderStatusKey = keyof typeof PurchaseOrderStatus;
export type PurchaseOrderStatusValue =
	(typeof PurchaseOrderStatus)[PurchaseOrderStatusKey];

/**
 * PD-14595: the single source of truth for PO status display labels.
 * Display vocabulary for '3' is "Issued" (owner decision, 2026-07-08):
 * the order was issued to the vendor. Internal identifiers (the enum
 * key RELEASED, the RBAC action key, the API route) deliberately keep
 * the release naming — the RBAC key is persisted in tenant role
 * grants, so renaming it would break existing permissions.
 * Render through t() at every display site.
 */
export const PurchaseOrderStatusLabels: Record<
	PurchaseOrderStatusValue,
	string
> = {
	[PurchaseOrderStatus.NEW]: 'New',
	[PurchaseOrderStatus.PENDING_APPROVAL]: 'Pending Approval',
	[PurchaseOrderStatus.APPROVED]: 'Approved',
	[PurchaseOrderStatus.RELEASED]: 'Issued',
	[PurchaseOrderStatus.PARTIALLY_RECEIVED]: 'Partially Received',
	[PurchaseOrderStatus.RECEIVED]: 'Received',
	[PurchaseOrderStatus.HELD]: 'Held',
	[PurchaseOrderStatus.CANCELLED]: 'Cancelled',
	[PurchaseOrderStatus.BACKORDERED]: 'Backordered',
	[PurchaseOrderStatus.REJECTED]: 'Rejected',
	[PurchaseOrderStatus.QUOTE]: 'Quote',
};

/**
 * PD-14595: status bands, consolidated from inline copies in crm-api /
 * PurchaseOrderDetail / ReceiveWizard / LocateStep. One truth each.
 */

/** Issued to the vendor and beyond — committed POs that may email/receive. */
export const PURCHASE_ORDER_ISSUED_BAND: ReadonlyArray<string> = [
	PurchaseOrderStatus.RELEASED,
	PurchaseOrderStatus.PARTIALLY_RECEIVED,
	PurchaseOrderStatus.RECEIVED,
	PurchaseOrderStatus.BACKORDERED,
];

/** Issued POs that can still receive stock (not fully received yet). */
export const PURCHASE_ORDER_RECEIVABLE_BAND: ReadonlyArray<string> = [
	PurchaseOrderStatus.RELEASED,
	PurchaseOrderStatus.PARTIALLY_RECEIVED,
	PurchaseOrderStatus.BACKORDERED,
];

/** Pre-issue statuses that may be hand-set to Held (PD-14569). */
export const PURCHASE_ORDER_HOLDABLE_STATUSES: ReadonlyArray<string> = [
	PurchaseOrderStatus.NEW,
	PurchaseOrderStatus.PENDING_APPROVAL,
	PurchaseOrderStatus.APPROVED,
	PurchaseOrderStatus.QUOTE,
];

/**
 * PD-16981: "open" purchase orders, as a person asking "which POs are still
 * open?" means it: every PO not finished. Received, Cancelled and Rejected
 * are finished; a Quote is not a purchase order yet.
 */
export const PURCHASE_ORDER_OPEN_STATUSES: ReadonlyArray<string> = [
	PurchaseOrderStatus.NEW,
	PurchaseOrderStatus.PENDING_APPROVAL,
	PurchaseOrderStatus.APPROVED,
	PurchaseOrderStatus.RELEASED,
	PurchaseOrderStatus.PARTIALLY_RECEIVED,
	PurchaseOrderStatus.HELD,
	PurchaseOrderStatus.BACKORDERED,
];

/** Statuses the issue (release) door accepts (PD-14566 / PD-14589). */
export const PURCHASE_ORDER_ISSUABLE_STATUSES: ReadonlyArray<string> = [
	PurchaseOrderStatus.QUOTE,
	PurchaseOrderStatus.NEW,
	PurchaseOrderStatus.APPROVED,
];

/**
 * Get human-readable status label from status code
 */
export function getPurchaseOrderStatusLabel(
	status: string | number | null | undefined,
): string {
	if (status == null) return '-';
	return (
		PurchaseOrderStatusLabels[String(status) as PurchaseOrderStatusValue] ??
		'Unknown'
	);
}

/**
 * Get status color based on status code
 */
export function getPurchaseOrderStatusColor(
	status: string | number | null | undefined,
): string {
	if (status == null) return 'default';

	const statusCode = String(status);
	switch (statusCode) {
		case PurchaseOrderStatus.NEW:
			return 'secondary'; // New
		case PurchaseOrderStatus.PENDING_APPROVAL:
			return 'warning'; // Pending Approval
		case PurchaseOrderStatus.APPROVED:
			return 'success'; // Approved
		case PurchaseOrderStatus.RELEASED:
			return 'info'; // Released
		case PurchaseOrderStatus.PARTIALLY_RECEIVED:
			return 'warning'; // Partially Received
		case PurchaseOrderStatus.RECEIVED:
			return 'success'; // Received
		case PurchaseOrderStatus.HELD:
			return 'warning'; // Held
		case PurchaseOrderStatus.CANCELLED:
			return 'danger'; // Cancelled
		case PurchaseOrderStatus.BACKORDERED:
			return 'secondary'; // Backordered
		case PurchaseOrderStatus.REJECTED:
			return 'danger'; // Rejected
		case PurchaseOrderStatus.QUOTE:
			return 'info'; // Quote
		default:
			return 'default';
	}
}
